import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import {
	ICreateCampaignRequest,
	ICreateSocialMediaPostRequest,
	ILocationState,
	TemplateOrTemplateFilter,
} from '../../../models';
import { CampaignType } from '../../../models/AdminModels';
import { BlogPostRequest } from '../../../models/Blogs';
import { useFullscreenModal, useUserSession } from '../../../models/hooks/appStateHooks';
import { getBlogPostAsync } from '../../../queries';
import { IBlogTemplateCard } from '../../../viewmodels/AppViewModels';
import { ContentCalendarTemplateBrowser } from './ContentCalendarTemplateBrowser';
import { styleSheet } from './styles';

type Props = {
	templateBrowserCampaignType: CampaignType;
	setTemplateBrowserCampaignType: (campaignType: CampaignType) => void;
	isSuggestionsOn?: boolean;
};

const ContentCalendarTemplateBrowserWrapperBase: React.FC<Props> = ({
	templateBrowserCampaignType,
	setTemplateBrowserCampaignType,
	isSuggestionsOn,
}) => {
	const fullscreenModal = useFullscreenModal();
	const userSession = useUserSession();

	const droppableType = 'campaignTemplateCard';

	const isBlogsEnabled = userSession.account?.features?.blogFeature?.enabled;
	const isSocialMediaEnabled = userSession.account?.features?.socialMedia?.enabled;

	const campaignTypes = React.useMemo<CampaignType[]>(() => {
		const types = [CampaignType.Email];
		if (isSocialMediaEnabled) {
			types.push(CampaignType.Social);
		}
		if (isBlogsEnabled) {
			types.push(CampaignType.Blog);
		}
		return types;
	}, [isBlogsEnabled, isSocialMediaEnabled]);

	const handleCampaignTemplateClicked = (
		template: Api.ITemplate,
		schedule?: Api.IScheduledSend,
		_referrer = 'contentCalendarAppDrawer'
	) => {
		if (template?.templateType === Api.TemplateType.Email || !template?.templateType) {
			const locationStateEmail: ILocationState<any, ICreateCampaignRequest<TemplateOrTemplateFilter>> = {
				model: {
					context: template,
					onFinish: (_didSend: boolean) => {
						// Email campaign finished
					},
					schedule,
					type: 'Template',
				},
			};
			fullscreenModal?.history.push({
				pathname: '/email/campaigns/create/from-template',
				state: locationStateEmail,
			});
		} else if (template.templateType === Api.TemplateType.Blog) {
			const locationState: ILocationState<any, BlogPostRequest> = {
				model: {
					schedule,
					template,
				},
			};
			fullscreenModal?.history.push({
				pathname: `/blog/post/create-from-template/${template.id}`,
				state: locationState,
			});
		} else if (template.templateType === Api.TemplateType.SocialMediaPost) {
			const locationStateSocial: ILocationState<any, ICreateSocialMediaPostRequest<Api.ITemplate>> = {
				model: {
					context: template,
					onFinish: (_didSend: boolean) => {
						// Social campaign finished
					},
					schedule,
					sendFromUser: userSession.user,
					type: 'Template',
				},
			};
			fullscreenModal?.history.push({
				pathname: `/social-media/post/create/from-template`,
				state: locationStateSocial,
			});
		}
	};
	const launchEditBlogPost = (post: Api.IBlogPost, schedule?: Api.IScheduledSend) => {
		const locationState: ILocationState<any, BlogPostRequest> = {
			model: {
				post,
				schedule,
				sendFromUser: userSession.user,
			},
		};
		const pathname = `/blog/post/edit/${post.id}`;
		fullscreenModal?.history?.push({
			pathname,
			state: locationState,
		});
	};
	const onBlogPostClicked = async (e: React.MouseEvent<HTMLElement>, blog: Api.IBlogReportView | IBlogTemplateCard) => {
		try {
			const result = await getBlogPostAsync({ id: blog.id, userSession });
			launchEditBlogPost(result);
		} catch {
			toast.error('Failed to load blog post');
		}
	};

	return (
		<div className={css(styleSheet.appDrawerTemplateBrowserContainer)}>
			<ContentCalendarTemplateBrowser
				isAppDrawer={true}
				dragDropProps={{
					droppableId: 'campaignTemplatesList',
					type: droppableType,
					draggingStyles: [styleSheet.campaignTemplateCardDragging],
				}}
				onTemplateTypeSelected={setTemplateBrowserCampaignType}
				onCampaignTemplateClicked={handleCampaignTemplateClicked}
				selectedCampaignType={templateBrowserCampaignType}
				campaignTypes={campaignTypes}
				isSuggestionsOn={isSuggestionsOn}
				onBlogPostEditClicked={onBlogPostClicked}
			/>
		</div>
	);
};

export const ContentCalendarTemplateBrowserWrapper = observer(ContentCalendarTemplateBrowserWrapperBase);
